/* Browser-independent draft coordinator. Storage, HTTP and timers are injected in tests. */
const clone = value => JSON.parse(JSON.stringify(value));
const equal = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const ROOT = '/api/objects/creation-draft';
const REQUEST = {ignoreModal: true, timeout: 20000};

function validForm(data) {
    if (!data || data.version !== 1 || !Number.isInteger(data.step) || data.step < 0 || data.step > 3 || !['new', 'existing'].includes(data.mode)) return false;
    const fields = {object: ['name', 'phone', 'address', 'country'], process: ['folder_path', 'report_time', 'chat_id'], contract: ['name', 'date'], telegram: ['name'], work_form: ['name'], group_form: ['name', 'group', 'unit', 'method', 'target', 'material', 'equipment']};
    if (Object.keys(fields).some(key => !data[key] || fields[key].some(field => typeof data[key][field] !== 'string'))) return false;
    return Array.isArray(data.targets) && data.targets.every(row => row && typeof row.name_project === 'string' && ['string', 'number'].includes(typeof row.value) &&
        (row.materials === undefined || (Array.isArray(row.materials) && row.materials.every(item => item && Number.isSafeInteger(Number(item.material_id)) && Number(item.material_id) > 0 && ['string', 'number'].includes(typeof item.value) && ['string', 'number'].includes(typeof item.limit))))) &&
        Array.isArray(data.telegram.participants) && data.telegram.participants.every(row => row && Number.isInteger(Number(row.user_id)));
}

function newId() {
    const bytes = new Uint8Array(16);
    window.crypto.getRandomValues(bytes);
    bytes[6] = (bytes[6] & 15) | 64;
    bytes[8] = (bytes[8] & 63) | 128;
    const hex = Array.from(bytes, b => b.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function emptyForm(company) {
    return {
        version: 1, step: 0, mode: 'new', building_id: null,
        object: {name: '', phone: '', address: '', country: company === 'aerodorstroy_ru' ? 'RU' : 'BY'},
        process: {main_staff_id: null, curator_id: null, has_access: [], folder_path: '', report_time: '09:00', chat_id: ''},
        contract: {name: '', date: ''}, targets: [],
        telegram: {expanded: true, mode: 'create', name: '', group_id: null, participants: [], custom: [], additional: {
            itr: {enabled: false, initialized: false, participants: []}, supply: {enabled: false, initialized: false, participants: []}, mechanics: {enabled: false, initialized: false, participants: []}
        }},
        work_form: {open: false, name: '', unit_of_measurement: '', group_id: null},
        group_form: {open: false, name: '', group: '', unit: '', method: '', target: '', material: '', equipment: ''}
    };
}

class DraftCoordinator {
    constructor({api, storage, tabStorage, company, userId, onState, onRestore, onCreated, canSend = () => true, uuid = newId, setTimer = (callback, delay) => setTimeout(callback, delay), clearTimer = timer => clearTimeout(timer)}) {
        Object.assign(this, {api, storage, tabStorage, company, userId, onState, onRestore, onCreated, canSend, uuid, setTimer, clearTimer});
        this.key = `argus.object-creation.v1:${company}:${userId}`;
        this.doc = {draft_id: uuid(), revision: 0, data: emptyForm(company), dirty: false, pending: null, action: null};
        this.status = 'idle'; this.localOk = true; this.ready = false; this.stopped = false;
        this.conflict = null; this.telegram = null; this.retry = 0; this.error = ''; this.fields = {}; this.readonly = false; this.loaded = false;
    }
    state() { return {status: this.status, localOk: this.localOk, ready: this.ready, error: this.error, fields: this.fields,
        recoveryPending: !!this.recoveryPending, recoveryName: this.doc.data.object.name, readonly: this.readonly, loaded: this.loaded, owner: `${this.company}:${this.userId}`,
        conflict: this.conflict, telegram: this.telegram, restored: !!this.restored, busy: !!this.doc.action, dirty: this.doc.dirty, connectionError: this.connectionError}; }
    emit() { if (!this.stopped) this.onState(this.state()); }
    restore() { if (!this.stopped && !this.holdRestore && !this.recoveryPending) this.onRestore(clone(this.doc.data)); }
    persist() {
        if (this.stopped) return;
        try { this.storage.setItem(this.key, JSON.stringify(this.doc)); this.localOk = true; }
        catch (error) { this.localOk = false; }
        // Per-tab recovery retains unsent edits when another tab writes the shared local copy.
        try { if (this.tabStorage) this.tabStorage.setItem(this.key, JSON.stringify(this.doc)); } catch (error) { /* Shared storage remains the durable copy. */ }
        this.emit();
    }
    cancelTimer() { if (this.timer) this.clearTimer(this.timer); this.timer = null; }
    schedule(delay = 800) {
        this.cancelTimer();
        if (this.stopped || !this.ready || this.recoveryPending || this.conflict || this.doc.action || this.readonly || !this.canSend()) return;
        this.timer = this.setTimer(() => { this.timer = null; this.flush().catch(() => {}); }, delay);
    }
    edit(data) {
        if (!this.ready || this.stopped || this.readonly || this.recoveryPending || this.status === 'completed' || this.doc.action || equal(data, this.doc.data)) return;
        this.doc.data = clone(data); this.doc.dirty = true; this.retry = 0;
        this.connectionAttempts = 0;
        if (this.status === 'saved') this.status = 'idle';
        this.persist(); this.schedule();
    }
    async open({offerRecovery = false} = {}) {
        this.holdRestore = offerRecovery;
        try {
            let local = JSON.parse(this.storage.getItem(this.key));
            try {
                const tab = this.tabStorage && JSON.parse(this.tabStorage.getItem(this.key));
                if (tab && (tab.dirty || tab.action) && validForm(tab.data)) local = tab;
            } catch (error) { /* Fall back to the shared copy. */ }
            if (local && validForm(local.data) && local.draft_id && Number.isInteger(local.revision)) {
                this.doc = local; this.restored = true;
            }
        } catch (error) { this.localOk = false; this.error = 'Не удалось прочитать локальный черновик'; }
        this.restore();
        if (this.doc.action) {
            // Unknown business outcomes require an explicit retry, including after reload.
            this.holdRestore = false; this.restore();
            this.ready = true; this.emit();
            await this.refresh();
            return;
        }
        await this.refresh();
        this.recoveryPending = offerRecovery && (!!this.conflict || (!!this.restored && !equal(this.doc.data, emptyForm(this.company))));
        this.holdRestore = false; this.restore();
        this.ready = true; this.emit();
        if (this.doc.dirty && !this.conflict) this.schedule();
    }
    resume() {
        if (!this.recoveryPending || this.stopped) return;
        this.recoveryPending = false; this.restore(); this.emit();
        if (this.doc.dirty) this.schedule();
    }
    async restartRecovery() {
        if (!this.recoveryPending || this.stopped || this.doc.action || this.readonly || !this.canSend()) return;
        const {data} = await this.api.get(ROOT, REQUEST);
        if (this.stopped) return;
        const row = data.draft, previous = this.serverHead;
        this.serverHead = row;
        if (row && row.status === 'active' && (!previous || previous.draft_id !== row.draft_id || previous.revision !== row.revision)) {
            this.setConflict(row);
            throw new Error('Серверный черновик изменился. Подтвердите «Начать заново» ещё раз или восстановите его.');
        }
        if (!row || row.status !== 'active') return this.reset(row ? row.revision : 0);
        // Discard the confirmed server revision directly, retaining local edits until it succeeds.
        this.doc.action = {type: 'discard', draft_id: row.draft_id, expected_revision: row.revision};
        this.persist(); return this.runAction();
    }
    accept(row) {
        if (!validForm(row.draft_data)) throw new Error('Формат серверного черновика не поддерживается. Ваш локальный ввод сохранён.');
        this.doc = {draft_id: row.draft_id, revision: row.revision, data: clone(row.draft_data), dirty: false, pending: null, action: null};
        this.telegram = row.telegram_result; this.restored = true; this.status = 'saved'; this.error = '';
        this.persist(); this.restore();
    }
    async refresh() {
        if (this.stopped || this.inFlight || this.actionFlight || this.readonly || !this.canSend()) return;
        try {
            const {data: response} = await this.api.get(ROOT, REQUEST);
            if (this.stopped) return;
            this.loaded = true;
            if (this.doc.action) { this.error = 'Результат действия неизвестен. Нажмите «Проверить результат».'; this.emit(); return; }
            const row = response.draft;
            this.serverHead = row;
            this.telegram = row && row.draft_id === this.doc.draft_id ? row.telegram_result : null;
            if (row && row.status === 'active') {
                if (row.draft_id === this.doc.draft_id && this.doc.pending && row.last_write_id === this.doc.pending.write_id) {
                    this.doc.revision = row.revision;
                    this.doc.dirty = !equal(this.doc.data, this.doc.pending.data);
                    this.doc.pending = null;
                    this.status = this.doc.dirty ? 'idle' : 'saved'; this.persist();
                } else if (!this.doc.dirty && !this.doc.pending) this.accept(row);
                else if (row.draft_id !== this.doc.draft_id || row.revision !== this.doc.revision) this.setConflict(row);
            } else if (this.doc.dirty && row && row.draft_id === this.doc.draft_id) {
                this.setConflict(row);
            } else if (!this.doc.dirty) {
                this.doc = {draft_id: this.uuid(), revision: row ? row.revision : 0, data: emptyForm(this.company), dirty: false, pending: null, action: null};
                this.restored = false;
                this.status = 'idle'; this.persist(); this.restore();
            } else if ((row ? row.revision : 0) !== this.doc.revision) this.setConflict(row);
            this.error = this.conflict ? 'Черновик изменён на другом устройстве' : '';
            this.emit();
        } catch (error) { this.failed(error); }
    }
    setConflict(row, receipt) {
        this.conflict = {draft: row || null, receipt: receipt || null};
        this.status = 'conflict'; this.cancelTimer(); this.emit();
    }
    failed(error) {
        const response = error.response;
        const body = response && response.data;
        this.connectionError = !response || response.status >= 500;
        if (response && [401, 403, 404].includes(response.status)) { this.readonly = true; this.cancelTimer(); }
        if (response && response.status === 409 && body && ['DRAFT_CONFLICT', 'DRAFT_CLOSED'].includes(body.code)) {
            this.setConflict(body.draft, body.receipt);
        } else {
            this.status = !response ? 'offline' : 'error';
            this.error = typeof body === 'string' && body.trim() ? body : body && body.message ||
                (!response || response.status >= 500 ? 'Сервер не ответил. Введённые данные остаются в форме. Проверим связь автоматически.' : error.message || 'Не удалось выполнить действие');
            this.fields = body && body.fields || {};
        }
        this.emit();
    }
    async flush() {
        this.cancelTimer();
        if (this.inFlight) { await this.inFlight; if (this.doc.dirty) return this.flush(); return; }
        if (!this.ready || this.stopped || this.recoveryPending || this.conflict || this.doc.action || !this.doc.dirty || this.readonly || !this.canSend()) return;
        if (!this.doc.pending) this.doc.pending = {draft_id: this.doc.draft_id, expected_revision: this.doc.revision, write_id: this.uuid(), data: clone(this.doc.data)};
        this.persist(); this.status = 'saving'; this.emit();
        const pending = clone(this.doc.pending);
        this.inFlight = this.api.put(ROOT, pending, REQUEST).then(({data}) => {
            this.doc.revision = data.draft.revision; this.telegram = data.draft.telegram_result;
            this.doc.pending = null; this.doc.dirty = !equal(this.doc.data, pending.data);
            this.status = this.doc.dirty ? 'idle' : 'saved'; this.error = ''; this.fields = {}; this.retry = 0;
            this.connectionAttempts = 0;
            this.persist();
        }).catch(error => {
            this.failed(error);
            const status = error.response && error.response.status;
            // These responses reject the payload before writing. A corrected form needs a new payload.
            if ([400, 413, 422].includes(status)) { this.doc.pending = null; this.persist(); }
            if ((!status || status >= 500) && !(error.response && error.response.data && error.response.data.code === 'DRAFT_SCHEMA_UNAVAILABLE') && this.retry < 3) {
                this.schedule([2000, 5000, 15000][this.retry++]);
            }
            throw error;
        });
        try { await this.inFlight; } finally { this.inFlight = null; }
        if (this.doc.dirty && !this.stopped) return this.flush();
    }
    async resolveConflict(useLocal) {
        if (!this.canSend() || this.readonly || this.stopped) return;
        if (!this.conflict) return;
        // Fetch again: choosing a version must not overwrite a third edit made meanwhile.
        const {data: response} = await this.api.get(ROOT, REQUEST);
        const row = response.draft;
        const displayed = this.conflict.draft;
        if ((row ? row.revision : 0) !== (displayed ? displayed.revision : 0)) { this.setConflict(row); return; }
        if (useLocal && (this.conflict.receipt || (row && row.status !== 'active'))) throw new Error('Черновик закрыт. Сначала начните новый объект.');
        this.conflict = null;
        if (!useLocal) {
            if (row && row.status === 'active') this.accept(row);
            else this.reset(row ? row.revision : 0);
            return;
        }
        this.doc.draft_id = row ? row.draft_id : this.doc.draft_id;
        this.doc.revision = row ? row.revision : 0; this.doc.pending = null; this.doc.dirty = true;
        this.persist(); await this.flush();
    }
    reset(revision) {
        this.recoveryPending = false;
        this.doc = {draft_id: this.uuid(), revision, data: emptyForm(this.company), dirty: false, pending: null, action: null};
        this.conflict = null; this.telegram = null; this.restored = false; this.status = 'idle'; this.error = ''; this.fields = {};
        this.persist(); this.restore();
    }
    async action(type) {
        if (!this.canSend() || this.readonly || this.stopped) return;
        if (this.recoveryPending) throw new Error('Восстановите черновик или начните заново');
        if (this.doc.action) return this.runAction();
        await this.flush();
        if (!this.canSend() || this.readonly || this.stopped) return;
        if (this.conflict || this.doc.dirty) throw new Error('Сначала сохраните черновик');
        if (type === 'discard' && this.status === 'idle' && equal(this.doc.data, emptyForm(this.company))) return this.reset(this.doc.revision);
        if (!this.doc.revision) { if (type === 'discard') return this.reset(0); throw new Error('Заполните данные объекта'); }
        this.doc.action = {type, draft_id: this.doc.draft_id, expected_revision: this.doc.revision, operation_id: this.uuid()};
        this.persist(); return this.runAction();
    }
    async runAction() {
        if (!this.canSend() || this.readonly || this.stopped) return;
        if (this.actionFlight) return this.actionFlight;
        const action = this.doc.action;
        if (!action) return;
        this.emit();
        const request = action.type === 'discard' ? this.api.delete(ROOT, Object.assign({data: action}, REQUEST))
            : this.api.post(`${ROOT}/${action.type === 'telegram' ? 'telegram-group' : 'submit'}`, action, REQUEST);
        this.actionFlight = request.then(({data}) => {
            this.doc.action = null; this.error = '';
            if (action.type === 'submit') {
                if (!this.stopped) {
                    try {
                        const local = JSON.parse(this.storage.getItem(this.key));
                        if (local && local.draft_id === action.draft_id) this.storage.removeItem(this.key);
                    } catch (error) { this.localOk = false; }
                    try { if (this.tabStorage) this.tabStorage.removeItem(this.key); } catch (error) { /* A terminal receipt still prevents replay. */ }
                }
                this.doc.dirty = false; this.status = 'completed';
                if (!this.stopped) this.onCreated(data.result);
            } else if (action.type === 'discard') this.reset(data.draft ? data.draft.revision : 0);
            else {
                this.telegram = data.draft.telegram_result;
                if (this.telegram && this.telegram.chatId) {
                    this.doc.data.process.chat_id = this.telegram.chatId;
                    this.doc.dirty = true; this.restore();
                }
                this.persist(); this.schedule();
            }
            this.emit();
        }).catch(error => {
            // A timeout is an unknown outcome. Keep the exact operation for a safe retry after reload.
            if (error.response && error.response.status < 500) this.doc.action = null;
            this.failed(error); this.persist(); throw error;
        }).finally(() => { this.actionFlight = null; });
        return this.actionFlight;
    }
    pauseConnection() { this.cancelTimer(); this.persist(); }
    async resumeConnection() {
        if (this.connectionFlight || (this.connectionAttempts || 0) >= 3) return;
        this.connectionFlight = true;
        try {
            if (this.inFlight) { try { await this.inFlight; } catch (_) { /* Retain the write id for retry. */ } }
            if (this.doc.action || this.readonly || this.stopped || !this.canSend() || this.conflict || this.recoveryPending || !this.doc.dirty) return;
            this.connectionAttempts = (this.connectionAttempts || 0) + 1;
            await this.flush();
        } finally { this.connectionFlight = false; }
    }
    close() { this.cancelTimer(); this.stopped = true; }
}
module.exports = {DraftCoordinator, emptyForm, newId, validForm};
